
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";

interface UsePhotoCropperProps {
  photos: string[];
  onPhotosChange: (photos: string[]) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
}

export const usePhotoCropper = ({ photos, onPhotosChange, fileInputRef }: UsePhotoCropperProps) => {
  const [cropperOpen, setCropperOpen] = useState(false);
  const [imageToEdit, setImageToEdit] = useState<File | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const { user } = useAuth();

  const openCropper = (file: File, index: number = 0) => {
    setImageToEdit(file);
    setEditingIndex(index);
    setCropperOpen(true);
  };

  const closeCropper = () => {
    setCropperOpen(false);
    setImageToEdit(null);
    setEditingIndex(null);
  };

  const handleCropComplete = async (croppedBlob: Blob, originalFileName: string) => {
    if (!user) return;

    try {
      const croppedFile = new File([croppedBlob], originalFileName, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      const { photoUploadService } = await import("@/services/photoUploadService");
      // Mark as main photo if it's the first photo (index 0)
      const isMainPhoto = editingIndex === 0;
      const uploadedUrl = await photoUploadService.uploadPhoto(croppedFile, user.id, isMainPhoto);
      
      if (editingIndex === 0) {
        const newPhotos = [uploadedUrl, ...photos.slice(1)];
        onPhotosChange(newPhotos);
      } else if (editingIndex !== null) {
        const newPhotos = [...photos];
        newPhotos[editingIndex] = uploadedUrl;
        onPhotosChange(newPhotos);
      } else {
        onPhotosChange([...photos, uploadedUrl]);
      }

      toast({
        title: "Photo uploaded successfully!",
        description: isMainPhoto ? "Your square main profile photo has been saved." : "Your cropped photo has been saved to your profile.",
      });
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      closeCropper();
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return {
    cropperOpen,
    imageToEdit,
    editingIndex,
    openCropper,
    closeCropper,
    handleCropComplete
  };
};
